import React, { forwardRef } from "react";
import PropTypes from 'prop-types'
import DOMPurify from "dompurify";

const SafeHTML = forwardRef(function SafeHTML(
    { html, sanitizeOptions, as: Tag, style, ...rest },
    ref
) {
    const isIframe =
        typeof Tag === "string" && Tag.toLowerCase() === "iframe";

    if (!isIframe) {
        const safe = DOMPurify.sanitize(html ?? "", sanitizeOptions)

        return (
            <Tag
                ref={ref}
                dangerouslySetInnerHTML={{ __html: safe }}
                style={style}
                {...rest}
            />
        );
    }

    const onIframeLoad =
        (e) => {
            const iframe = e.currentTarget;

            try {
                const doc = iframe?.contentDocument;
                if (doc) {
                    // esnure no margin/scroll inside the iframe
                    doc.documentElement.style.margin = "0";
                    doc.documentElement.style.padding = "0";
                    doc.documentElement.style.overflow = "hidden";
                    doc.body.style.margin = "0";
                    doc.body.style.padding = "0";
                    doc.body.style.overflow = "hidden";

                    // resize iframe to content height
                    const height =
                        doc.documentElement.scrollHeight || doc.body.scrollHeight || 0;
                    iframe.style.height = `${height}px`;
                }
            } catch {
                // ignore cross-origin failures
            }
        }

    const srcDoc = `
            <!doctype html>
            <html>
            <head>
                <meta charset="utf-8"/>
                <meta name="viewport" content="width=device-width,initial-scale=1"/>
                <base href="./">
                <style>
                html, body { margin:0; padding:0; overflow:hidden; }
                body { display:block; }
                </style>
            </head>
            <body>${html}</body>
            </html>
        `;

    const safe = DOMPurify.sanitize(srcDoc ?? "", sanitizeOptions)

    return (
        <iframe
            srcDoc={safe}
            sandbox="allow-same-origin"
            style={{
                width: "100%",
                border: 0,
                display: "block",
                overflow: "hidden", // allow parent scrolling only
                ...(style || {}),
            }}
            onLoad={onIframeLoad}
            {...rest}
        />
    );
});

SafeHTML.propTypes = {
    html: PropTypes.string,
    sanitizeOptions: PropTypes.object,
    as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
    style: PropTypes.object,
};

SafeHTML.defaultProps = {
    html: "",
    sanitizeOptions: { USE_PROFILES: { html: true } },
    as: "div",
};

export default SafeHTML;